import { EventEmitter, Inject, Injectable } from '@angular/core';

import { Choice } from 'app/dialogs/dialog-buttons.component';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitter } from 'core/utils/async-event-emitter';
import { CriticalError, ErrorBase, TrivialError } from './error';

export class HttpOrSyntaxError extends CriticalError
{
  protected _name: string = 'HttpOrSyntaxError';
}

@Injectable()
export class ErrorService
{
  public criticalErrorOccurred: EventEmitter<CriticalError> =
      new EventEmitter();
  public errorsOccurred: AsyncEventEmitter<Array<ErrorBase>, Choice|undefined>
      = new AsyncEventEmitter();

  private errors: {[origin: string]: {[type: string]: Array<ErrorBase> }} = {};

  constructor(
      @Inject(LoggingService) private loggingService: LoggingService,
      @Inject(TranslationService) private translationService)
  {}

  public add(error: ErrorBase,
             origin: string = 'default',
             shouldLogError: boolean = true): void
  {
    if (shouldLogError) {
      this.logError(error, origin);
    }

    if (error instanceof CriticalError) {
      this.criticalErrorOccurred.emit(error);
    }
    else {
      if (!this.errors.hasOwnProperty(origin)) {
        this.errors[origin] = {};
      }
      if (!this.errors[origin].hasOwnProperty(error.name)) {
        this.errors[origin][error.name] = [];
      }

      const translateMessage = (reference, message, name) => {
        const languageKey = this.translationService.getLanguageKeyPrefix(
            'error', name);
        return this.translationService.translateErrorMessage(
            languageKey, reference) || message;
      };

      if (this.errors[origin][error.name].filter(e => {
          return translateMessage(e.reference, e.message, e.name) ===
              translateMessage(error.reference, error.message, error.name);
          }).length < 1) {
        this.errors[origin][error.name].push(error);
      }
    }
  }

  public clear(): void
  {
    this.errors = {};
    this.criticalErrorOccurred.emit(undefined);
  }

  public getErrors(): any
  {
    return this.errors;
  }

  public async flushByType(origin: string = 'default'
                          ): Promise<Array<Choice>>
  {
    let results: Array<Choice> = [];
    if (this.errors[origin]) {
      for (const type in this.errors[origin]) {
        if (type in this.errors[origin]) {
          results.push(this.firstChoice(
              await this.errorsOccurred.emit(this.errors[origin][type])));
        }
      }
      delete this.errors[origin];
    }

    return results;
  }

  public async flush(origin: string = 'default'): Promise<Choice|undefined>
  {
    let result: Choice|undefined = undefined;
    if (this.errors[origin]) {
      const occurred = Utils.objectValuesToArray(this.errors[origin])
        .reduce((acc, val) => acc.concat(val), []);
      result = this.firstChoice(await this.errorsOccurred.emit(occurred));
      delete this.errors[origin];
    }
    return result;
  }

  public async flushAll(): Promise<Choice|undefined>
  {
    let errors: Array<ErrorBase> = [];
    for (let origin in this.errors) {
      if (this.errors[origin]) {
        errors = errors.concat(Utils.objectValuesToArray(this.errors[origin])
          .reduce((acc, val) => acc.concat(val), []));
        delete this.errors[origin];
      }
    }

    if (errors.length === 0) {
      return undefined;
    }
    return this.firstChoice(await this.errorsOccurred.emit(errors));
  }

  private firstChoice(choices: Array<Choice|undefined>): Choice|undefined
  {
    return choices.find(choice => choice !== undefined);
  }

  private logError(error: ErrorBase, origin: string): void
  {
    let severity: string;
    if (error instanceof TrivialError) {
      severity = Severity.WARNING;
    }
    else if (error instanceof CriticalError) {
      severity = Severity.ERROR;
    }
    else {
      this.logError(
        new TrivialError('received unkown error: ' + error),
        origin);
      severity = Severity.ERROR;
    }

    const reference = error.reference || [];
    const message = `${error.message} / ${reference.join(', ')}`;
    this.loggingService.log(origin, severity, message);
  }

  /**
   * @throws HttpOrSyntaxError
   */
  public async catchHttpOrSyntaxError(error: Error | Response,
                                      origin: string): Promise<void>
  {
    if (error.hasOwnProperty('status') &&
        error.hasOwnProperty('statusText') &&
        error.hasOwnProperty('url')
    ) {
      error = <Response> error;
      error = new HttpOrSyntaxError(
          '[' + error.status + '] "' +
          error.url + '" ' + error.statusText + ' (' + origin + ')');
    }
    else {
      error = <Error> error;
      error = new HttpOrSyntaxError(error.message + ' (' + origin + ')');
    }

    this.add(<ErrorBase> error, origin);
    await this.flush(origin);
    throw error;
  }
}
